// imported from myintelli-onnx commit 9f78984 on 2026-05-04
// do not edit manually — regenerate via `node scripts/sync-onnx-vendor.mjs`
/**
 * Thin wrappers over the ONNX runtime for detector / embedder / antispoof.
 *
 * These are intentionally less detailed than the Python equivalents — the
 * heavy lifting (tensor preparation) lives at the platform adapter so each
 * runtime can use its native fast paths. Decoding logic (anchor decoding,
 * NMS, alignment) is shared in pure JS.
 */

import type {
  AntiSpoofConfig,
  DetectorConfig,
  EmbedderConfig,
} from './config.js';
import {
  getRuntime,
  type InferenceSession,
  type Tensor,
} from './platform/runtime.js';
import type {
  Bbox,
  FaceDetection,
  ImageFrame,
  LivenessDecision,
  Landmarks5,
} from './types.js';

// ─── Detector ────────────────────────────────────────────────────────────

const SCRFD_STRIDES = [8, 16, 32] as const;
const SCRFD_NUM_ANCHORS = 2;

export class Detector {
  private session: InferenceSession | null = null;
  private readonly anchorCache = new Map<string, Float32Array>();

  constructor(private readonly cfg: DetectorConfig) {}

  async load(modelBytes: ArrayBuffer | Uint8Array | string): Promise<void> {
    this.session = await getRuntime().createSession(modelBytes);
  }

  async detect(frame: ImageFrame): Promise<FaceDetection[]> {
    if (!this.session) throw new Error('Detector not loaded; call load() first');
    const { input, scale } = letterboxAndNormalize(frame, this.cfg.inputSize);
    const tensor = getRuntime().createTensor(input, [1, 3, this.cfg.inputSize, this.cfg.inputSize]);
    const outputs = await this.session.run({ [this.session.inputNames[0]!]: tensor });

    const ordered = this.session.outputNames.map((n) => outputs[n]!);
    return this.decode(ordered, scale);
  }

  async release(): Promise<void> {
    await this.session?.release();
    this.session = null;
  }

  private decode(outputs: Tensor[], scale: number): FaceDetection[] {
    if (outputs.length !== 9) {
      throw new Error(
        `Expected 9 SCRFD outputs (scores×3, bbox×3, kps×3); got ${outputs.length}`,
      );
    }
    const detections: FaceDetection[] = [];
    for (let s = 0; s < SCRFD_STRIDES.length; s++) {
      const stride = SCRFD_STRIDES[s]!;
      const scores = outputs[s]!.data as Float32Array;
      const bboxRaw = outputs[s + 3]!.data as Float32Array;
      const kpsRaw = outputs[s + 6]!.data as Float32Array;
      const anchors = this.getAnchors(stride);

      const numAnchors = scores.length;
      for (let a = 0; a < numAnchors; a++) {
        const score = scores[a]!;
        if (score < this.cfg.scoreThreshold) continue;
        const ax = anchors[a * 2]!;
        const ay = anchors[a * 2 + 1]!;
        const x1 = (ax - bboxRaw[a * 4]! * stride) / scale;
        const y1 = (ay - bboxRaw[a * 4 + 1]! * stride) / scale;
        const x2 = (ax + bboxRaw[a * 4 + 2]! * stride) / scale;
        const y2 = (ay + bboxRaw[a * 4 + 3]! * stride) / scale;
        const lmk = [
          [(ax + kpsRaw[a * 10]! * stride) / scale, (ay + kpsRaw[a * 10 + 1]! * stride) / scale],
          [(ax + kpsRaw[a * 10 + 2]! * stride) / scale, (ay + kpsRaw[a * 10 + 3]! * stride) / scale],
          [(ax + kpsRaw[a * 10 + 4]! * stride) / scale, (ay + kpsRaw[a * 10 + 5]! * stride) / scale],
          [(ax + kpsRaw[a * 10 + 6]! * stride) / scale, (ay + kpsRaw[a * 10 + 7]! * stride) / scale],
          [(ax + kpsRaw[a * 10 + 8]! * stride) / scale, (ay + kpsRaw[a * 10 + 9]! * stride) / scale],
        ] as unknown as Landmarks5;
        detections.push({ bbox: [x1, y1, x2, y2] as Bbox, score, landmarks: lmk });
      }
    }
    const kept = nms(detections, this.cfg.nmsThreshold);
    kept.sort((a, b) => b.score - a.score);
    return kept.slice(0, this.cfg.maxFaces);
  }

  private getAnchors(stride: number): Float32Array {
    const featSize = this.cfg.inputSize / stride;
    const key = `${stride}:${featSize}`;
    const cached = this.anchorCache.get(key);
    if (cached) return cached;
    const out = new Float32Array(featSize * featSize * SCRFD_NUM_ANCHORS * 2);
    let idx = 0;
    for (let y = 0; y < featSize; y++) {
      for (let x = 0; x < featSize; x++) {
        const cx = (x + 0.5) * stride;
        const cy = (y + 0.5) * stride;
        for (let a = 0; a < SCRFD_NUM_ANCHORS; a++) {
          out[idx++] = cx;
          out[idx++] = cy;
        }
      }
    }
    this.anchorCache.set(key, out);
    return out;
  }
}

// ─── Embedder ────────────────────────────────────────────────────────────

export class Embedder {
  private session: InferenceSession | null = null;

  constructor(private readonly cfg: EmbedderConfig) {}

  async load(modelBytes: ArrayBuffer | Uint8Array | string): Promise<void> {
    this.session = await getRuntime().createSession(modelBytes);
  }

  /**
   * Embed a 112x112 aligned face crop. Caller is responsible for alignment —
   * pure-JS similarity transform is in alignment.ts.
   */
  async embed(alignedBgr: ImageFrame): Promise<Float32Array> {
    if (!this.session) throw new Error('Embedder not loaded');
    if (alignedBgr.width !== this.cfg.inputSize || alignedBgr.height !== this.cfg.inputSize) {
      throw new Error(
        `Embedder expects ${this.cfg.inputSize}x${this.cfg.inputSize}, got ${alignedBgr.width}x${alignedBgr.height}`,
      );
    }
    const input = bgrToNchw01ToMinusOnePlusOne(alignedBgr);
    const tensor = getRuntime().createTensor(input, [1, 3, this.cfg.inputSize, this.cfg.inputSize]);
    const outputs = await this.session.run({ [this.session.inputNames[0]!]: tensor });
    const raw = outputs[this.session.outputNames[0]!]!.data as Float32Array;
    return l2Normalize(raw);
  }

  async release(): Promise<void> {
    await this.session?.release();
    this.session = null;
  }
}

// ─── Anti-spoof ──────────────────────────────────────────────────────────

export class AntiSpoof {
  private session: InferenceSession | null = null;

  constructor(
    private readonly cfg: AntiSpoofConfig,
    readonly cropScale = 2.7,
  ) {}

  async load(modelBytes: ArrayBuffer | Uint8Array | string): Promise<void> {
    this.session = await getRuntime().createSession(modelBytes);
  }

  /** Score an 80x80 crop of the face region (already cropped + resized). */
  async score(crop80: ImageFrame): Promise<LivenessDecision> {
    if (!this.session) throw new Error('AntiSpoof not loaded');
    if (crop80.width !== this.cfg.inputSize || crop80.height !== this.cfg.inputSize) {
      throw new Error(
        `AntiSpoof expects ${this.cfg.inputSize}x${this.cfg.inputSize}, got ${crop80.width}x${crop80.height}`,
      );
    }
    const input = bgrToNchw01(crop80);
    const tensor = getRuntime().createTensor(input, [1, 3, this.cfg.inputSize, this.cfg.inputSize]);
    const outputs = await this.session.run({ [this.session.inputNames[0]!]: tensor });
    const raw = outputs[this.session.outputNames[0]!]!.data as Float32Array;
    const probs = softmax(raw);
    const liveScore = probs[1] ?? 0; // class 1 = live
    let verdict: LivenessDecision['verdict'];
    if (liveScore >= this.cfg.passiveHigh) verdict = 'live';
    else if (liveScore <= this.cfg.passiveLow) verdict = 'spoof';
    else verdict = 'borderline';
    return { score: liveScore, verdict, cropScale: this.cropScale };
  }

  async release(): Promise<void> {
    await this.session?.release();
    this.session = null;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function letterboxAndNormalize(
  frame: ImageFrame,
  size: number,
): { input: Float32Array; scale: number } {
  const scale = size / Math.max(frame.height, frame.width);
  const nw = Math.round(frame.width * scale);
  const nh = Math.round(frame.height * scale);
  // Naive bilinear resize — platform adapters can override if perf matters.
  const resized = bilinearResize(frame, nw, nh);
  // Pad to size×size (top-left aligned), normalize to [-1, 1] in CHW order.
  const out = new Float32Array(3 * size * size);
  const planeSize = size * size;
  const stride = frame.format === 'rgba' ? 4 : 3;
  for (let y = 0; y < nh; y++) {
    for (let x = 0; x < nw; x++) {
      const i = (y * nw + x) * stride;
      // SCRFD wants RGB
      const r = resized[i] ?? 0;
      const g = resized[i + 1] ?? 0;
      const b = resized[i + 2] ?? 0;
      const dst = y * size + x;
      out[dst] = (r - 127.5) / 128;
      out[planeSize + dst] = (g - 127.5) / 128;
      out[2 * planeSize + dst] = (b - 127.5) / 128;
    }
  }
  return { input: out, scale };
}

function bgrToNchw01ToMinusOnePlusOne(frame: ImageFrame): Float32Array {
  const { width: w, height: h } = frame;
  const out = new Float32Array(3 * w * h);
  const planeSize = w * h;
  const stride = frame.format === 'rgba' ? 4 : 3;
  for (let i = 0; i < w * h; i++) {
    const src = i * stride;
    let r: number;
    let g: number;
    let b: number;
    if (frame.format === 'bgr') {
      b = frame.data[src] ?? 0;
      g = frame.data[src + 1] ?? 0;
      r = frame.data[src + 2] ?? 0;
    } else {
      r = frame.data[src] ?? 0;
      g = frame.data[src + 1] ?? 0;
      b = frame.data[src + 2] ?? 0;
    }
    out[i] = (r / 255 - 0.5) / 0.5;
    out[planeSize + i] = (g / 255 - 0.5) / 0.5;
    out[2 * planeSize + i] = (b / 255 - 0.5) / 0.5;
  }
  return out;
}

function bgrToNchw01(frame: ImageFrame): Float32Array {
  const { width: w, height: h } = frame;
  const out = new Float32Array(3 * w * h);
  const planeSize = w * h;
  const stride = frame.format === 'rgba' ? 4 : 3;
  for (let i = 0; i < w * h; i++) {
    const src = i * stride;
    out[i] = frame.data[src] ?? 0;
    out[planeSize + i] = frame.data[src + 1] ?? 0;
    out[2 * planeSize + i] = frame.data[src + 2] ?? 0;
  }
  return out;
}

function bilinearResize(frame: ImageFrame, nw: number, nh: number): Uint8Array {
  const stride = frame.format === 'rgba' ? 4 : 3;
  const out = new Uint8Array(nw * nh * stride);
  for (let y = 0; y < nh; y++) {
    const sy = (y + 0.5) * (frame.height / nh) - 0.5;
    const sy0 = Math.max(0, Math.floor(sy));
    const sy1 = Math.min(frame.height - 1, sy0 + 1);
    const dy = sy - sy0;
    for (let x = 0; x < nw; x++) {
      const sx = (x + 0.5) * (frame.width / nw) - 0.5;
      const sx0 = Math.max(0, Math.floor(sx));
      const sx1 = Math.min(frame.width - 1, sx0 + 1);
      const dx = sx - sx0;
      for (let c = 0; c < stride; c++) {
        const v00 = frame.data[(sy0 * frame.width + sx0) * stride + c] ?? 0;
        const v10 = frame.data[(sy0 * frame.width + sx1) * stride + c] ?? 0;
        const v01 = frame.data[(sy1 * frame.width + sx0) * stride + c] ?? 0;
        const v11 = frame.data[(sy1 * frame.width + sx1) * stride + c] ?? 0;
        out[(y * nw + x) * stride + c] = Math.round(
          v00 * (1 - dx) * (1 - dy) + v10 * dx * (1 - dy) + v01 * (1 - dx) * dy + v11 * dx * dy,
        );
      }
    }
  }
  return out;
}

function softmax(x: Float32Array): Float32Array {
  let max = -Infinity;
  for (const v of x) if (v > max) max = v;
  let sum = 0;
  const out = new Float32Array(x.length);
  for (let i = 0; i < x.length; i++) {
    out[i] = Math.exp((x[i] ?? 0) - max);
    sum += out[i]!;
  }
  if (sum > 0) for (let i = 0; i < out.length; i++) out[i]! /= sum;
  return out;
}

function l2Normalize(x: Float32Array): Float32Array {
  let n = 0;
  for (const v of x) n += v * v;
  n = Math.sqrt(n);
  if (n < 1e-12) throw new Error('L2 norm collapsed');
  const out = new Float32Array(x.length);
  for (let i = 0; i < x.length; i++) out[i] = (x[i] ?? 0) / n;
  return out;
}

function nms(dets: FaceDetection[], iouThresh: number): FaceDetection[] {
  if (dets.length === 0) return [];
  const sorted = [...dets].sort((a, b) => b.score - a.score);
  const kept: FaceDetection[] = [];
  for (const d of sorted) {
    let drop = false;
    for (const k of kept) {
      if (iou(d.bbox, k.bbox) >= iouThresh) {
        drop = true;
        break;
      }
    }
    if (!drop) kept.push(d);
  }
  return kept;
}

function iou(a: Bbox, b: Bbox): number {
  const ix1 = Math.max(a[0], b[0]);
  const iy1 = Math.max(a[1], b[1]);
  const ix2 = Math.min(a[2], b[2]);
  const iy2 = Math.min(a[3], b[3]);
  const iw = Math.max(0, ix2 - ix1);
  const ih = Math.max(0, iy2 - iy1);
  const inter = iw * ih;
  const aa = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);
  const bb = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
  const u = aa + bb - inter;
  return u > 0 ? inter / u : 0;
}
